import { Component, type ErrorInfo, type ReactNode } from 'react'

const RELOAD_FLAG_KEY = 'chunk-reload-attempted'
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Loading chunk \d+ failed|Importing a module script failed/i

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? error.message : String(error)
  return CHUNK_ERROR_PATTERN.test(message)
}

/**
 * 앱 최상위 ErrorBoundary.
 *
 * 1) Dynamic import(code-split 청크) 로드 실패를 잡아 자동 리로드로 복구한다.
 *    - 배포 직후 탭을 열어둔 사용자가 새 index.html 로 교체된 서버에서 이전 해시 청크를
 *      받지 못해 "Failed to fetch dynamically imported module" 흰 화면이 뜨는 문제를 막는다.
 *    - sessionStorage 플래그로 무한 리로드를 방지한다.
 * 2) Chunk 에러가 아닌 일반 런타임 에러도 흰 화면 대신 사용자에게 보이는 에러 UI 로 전환한다.
 */
export class ChunkLoadErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env?.DEV) {
      console.error('[ChunkLoadErrorBoundary]', error, info)
    }

    if (isChunkLoadError(error)) {
      let alreadyReloaded = false
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG_KEY) === '1'
      } catch {
        /* storage 접근 불가 환경은 무시 */
      }

      if (!alreadyReloaded) {
        try {
          sessionStorage.setItem(RELOAD_FLAG_KEY, '1')
        } catch {
          /* noop */
        }
        window.location.reload()
      }
    }
  }

  private handleRetry = (): void => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG_KEY)
    } catch {
      /* noop */
    }
    window.location.reload()
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    const chunkError = isChunkLoadError(error)
    const title = chunkError ? '페이지를 불러오지 못했습니다' : '일시적인 오류가 발생했습니다'
    const description = chunkError
      ? '최신 버전의 페이지를 불러오는 중 문제가 발생했습니다. 새로고침 버튼을 눌러 다시 시도해 주세요.'
      : '잠시 후 다시 시도해 주세요. 문제가 계속되면 관리자에게 문의해 주세요.'

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 p-6">
        <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-lg border bg-card p-8 text-center shadow-sm">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="mt-2 inline-flex h-10 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            새로고침
          </button>
        </div>
      </div>
    )
  }
}
